import React, { createContext, useContext, useEffect, useState } from 'react';
import { compose } from 'redux';
import { func, bool } from 'prop-types';
import { APPOINTMENT_PENDING_STATUS } from '../../util/transaction';
import { injectIntl } from '../../util/reactIntl';
import AppointmentSchedulerContent from './AppointmentSchedulerContent';
import { useDispatch } from 'react-redux';
import { sendAppointment as sendAppointmentThunk } from '../../containers/InboxPage/InboxPage.duck';

export const MODAL_REQUEST_TO_APPOINTMENT = 'request';
export const MODAL_SCHEDULE_APPOINTMENT = 'schedule';

export const TOOLTIP_IS_PENDING = 'pending'; /** an appointment has already been sent*/
export const TOOLTIP_IS_ACTIVE = 'active'; /** appointments may be sent */

const PROVIDER_ROLE = 'provider';
const CUSTOMER_ROLE = 'customer';

export const AppointmentSchedulerContext = createContext(null);

export const useAppointmentSchedulerContext = () => {
    const context = useContext(AppointmentSchedulerContext);
    if (!context) {
        throw new Error(
            'AppointmentScheduler components must be rendered inside the AppointmentScheduler roo component.'
        );
    }
    return context;
};

const AppointmentScheduler = ({
    appointmentInProgress,
    currentTransaction,
    currentUser,
    isCustomer,
    intl,
    otherParty,
    ...rest
}) => {
    const dispatch = useDispatch();
    const handleAppointmentSending = appointmentData =>
        dispatch(sendAppointmentThunk(appointmentData));

    const {
        setAppointmentSchedulerModalVisibility,
        hasPendingAppointment,
    } = useAppointmentSchedulerContext();

    const [modalContentType, setModalType] = useState(MODAL_SCHEDULE_APPOINTMENT);
    const [tooltipContentType, setTooltipContentType] = useState(MODAL_SCHEDULE_APPOINTMENT);
    const [tooltipIsOpened, setTooltipIsOpened] = useState(false);
    const [mounted, setMounted] = useState(false);

    const transactionId = currentTransaction.id.uuid;

    const toggleModal = (otherStateFieldsToChange = {}) => {
        if (appointmentInProgress) return;

        setAppointmentSchedulerModalVisibility(prevState => !prevState);

        if (otherStateFieldsToChange.tooltipIsOpened) {
            setTooltipIsOpened(true);
        }
    };

    const handleAppointmentRequest = (isPending = false) => {
        if (isPending) {
            setTooltipIsOpened(true);
            setTooltipContentType(TOOLTIP_IS_PENDING);
            return;
        }

        setTooltipIsOpened(false);
        setAppointmentSchedulerModalVisibility(true);
        setModalType(MODAL_SCHEDULE_APPOINTMENT);
    };

    const handleFormSubmit = fields => {
        const {
            location: {
                selectedPlace: { address_components },
            },
            date: { date },
            time,
            message = '',
        } = fields;

        const findAddressCompByType = typeStr => ({ types, short_name }) =>
            types.includes(typeStr) && short_name;

        const composeAddressUnit = (units, delimiter = ' ') =>
            units.filter(s => !!s).join(delimiter);

        const { short_name: streetname } =
            address_components.find(findAddressCompByType('route')) || {};
        const { short_name: streetnumber } =
            address_components.find(findAddressCompByType('street_number')) || {};
        const { short_name: postalcode } =
            address_components.find(findAddressCompByType('postal_code')) || {};
        const { short_name: location } =
            address_components.find(findAddressCompByType('locality')) || {};

        const street = composeAddressUnit([streetname, streetnumber]);
        const locality = composeAddressUnit([postalcode, location].filter(s => !!s));

        const finalData = {
            transactionId: transactionId,
            sender: {
                userRole: isCustomer ? CUSTOMER_ROLE : PROVIDER_ROLE,
                userId: currentUser.id.uuid,
            },
            status: APPOINTMENT_PENDING_STATUS,
            data: {
                date,
                time,
                location: composeAddressUnit([street, locality], ', '),
                message,
            },
            readMessage: false,
        };

        handleAppointmentSending({
            ...finalData,
            otherParty,
            currentUser,
            currentListing: rest.currentListing,
            intl,
        });

        toggleModal();
    };

    useEffect(() => {
        if (hasPendingAppointment) {
            setTooltipContentType(TOOLTIP_IS_PENDING);
        }
        setMounted(true);
    }, []);

    return mounted ? (
        <AppointmentSchedulerContent
            handleAppointmentRequest={() => handleAppointmentRequest(hasPendingAppointment)}
            handleTooltipVisibility={flag => setTooltipIsOpened(flag)}
            toggleModal={(...args) => toggleModal(args)}
            handleModalTypeChange={modalContentType => setModalType(modalContentType)}
            handleFormSubmit={handleFormSubmit}
            modalContentType={modalContentType}
            tooltipContentType={tooltipContentType}
            tooltipIsOpened={tooltipIsOpened}
            {...rest}
        />
    ) : null;
};

AppointmentScheduler.propTypes = {
    appointmentInProgress: bool,
};

export default compose(injectIntl)(AppointmentScheduler);
